import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, AlertCircle, Loader, ArrowRight } from 'lucide-react';
import { BRAND } from '../../config/branding';

export default function EmailUnsubscribePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [unsubscribed, setUnsubscribed] = useState(false);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState('');

  useEffect(() => {
    const performUnsubscribe = async () => {
      try {
        setLoading(true);
        
        // Call the API endpoint to process the unsubscribe
        const response = await fetch(`${import.meta.env.VITE_API_URL}/drip/unsubscribe/${token}`);
        
        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.message || data.error || 'Failed to unsubscribe');
        }

        const data = await response.json();
        setEmail(data.email || '');
        setUnsubscribed(true);
      } catch (err) {
        setError(err.message || 'Failed to process unsubscribe');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      performUnsubscribe();
    }
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Processing your request...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-sm border p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Unable to Unsubscribe</h1>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-900 font-medium">Error: {error}</p>
              <p className="text-red-800 text-sm mt-2">
                The unsubscribe link may have expired. This can happen if you use the same link more than once.
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
              <h2 className="font-semibold text-blue-900 mb-2">What you can do:</h2>
              <ul className="text-blue-800 space-y-2 text-sm">
                <li>• Check for a recent email from {BRAND.name} and use the fresh unsubscribe link</li>
                <li>• Contact our team at support@stratel.com with your request</li>
                <li>• Use your email preferences link to manage your subscription settings</li>
              </ul>
            </div>

            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
            >
              Back to Home
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-xl shadow-sm border p-8">
          {/* Success Header */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Successfully Unsubscribed</h1>
              <p className="text-gray-600">You've been removed from our mailing list</p>
            </div>
          </div>

          {/* Confirmation Message */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
            <p className="text-green-900 font-medium">
              ✓ You will no longer receive emails from {BRAND.name} campaigns
            </p>
            <p className="text-green-800 text-sm mt-2">
              This may take a few minutes to take effect. If you continue to receive emails, please contact our support team.
            </p>
          </div>

          {/* What happens next */}
          <div className="mb-8">
            <h2 className="font-semibold text-gray-900 mb-4">What happens next?</h2>
            <div className="space-y-3">
              <div className="flex gap-3">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-blue-600 text-sm font-semibold">1</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Your unsubscribe is processed</p>
                  <p className="text-gray-600 text-sm">Your email address has been marked as unsubscribed in our system</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-blue-600 text-sm font-semibold">2</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">You'll stop receiving emails</p>
                  <p className="text-gray-600 text-sm">Existing email campaigns will skip your address from this point forward</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-blue-600 text-sm font-semibold">3</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">You can always change your mind</p>
                  <p className="text-gray-600 text-sm">If you'd like to re-subscribe or adjust your preferences, contact our team</p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer with contact info */}
          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <p className="text-gray-700 font-medium mb-3">Have questions?</p>
            <p className="text-gray-600 text-sm mb-4">
              If you'd like to manage your email preferences or have concerns, please reach out to our team:
            </p>
            <div className="space-y-2">
              <p className="text-gray-700">
                <span className="font-medium">Email:</span>{' '}
                <a href="mailto:support@stratel.com" className="text-blue-600 hover:underline">
                  support@stratel.com
                </a>
              </p>
              <p className="text-gray-700">
                <span className="font-medium">Website:</span>{' '}
                <a href="https://stratel.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  stratel.com
                </a>
              </p>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex gap-4">
            <Link
              to="/"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors text-center"
            >
              Back to Home
            </Link>
            <a
              href="mailto:support@stratel.com"
              className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-900 font-semibold py-3 px-6 rounded-lg transition-colors text-center"
            >
              Contact Support
            </a>
          </div>
        </div>

        {/* Privacy note */}
        <div className="text-center mt-8 text-gray-600 text-sm">
          <p>
            We respect your privacy. For more information, see our{' '}
            <a href="/privacy" className="text-blue-600 hover:underline">
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
